import React from 'react';
import resourceProp from 'shared/helpers/resource-prop';
import Episode from 'resources/episode';
import { resolve } from 'acast-www-utils';
import { Button, Text } from '@acast/decibel';
import { TimesLightGlyph, PlusLightGlyph } from '@acast/decibel-glyphs';
import { presentModal } from 'services/modals';
import { updateEntity } from 'app/entity';
import {
  getCategoryLabel,
  getAvailableCategories,
  getEarlyReleaseDayCount,
  PermissionCategory,
} from 'shared/helpers/acast-plus';
import { List, ListItem } from '../list';
import AddNewDialog from './add-condition-dialog';

const createDialogOnDismissHandler = episode => {
  return value => {
    if (value) {
      const conditions = resolve(episode, 'data.conditions', []).filter(
        condition => condition.category !== value.category
      );

      updateEntity(
        Episode,
        episode.data.merge({
          conditions: conditions.concat(value),
        })
      );
    }
  };
};

const showAddNewConditionDialog = episode => {
  presentModal(<AddNewDialog episode={episode} />).then(createDialogOnDismissHandler(episode));
};

const getConditionShortDescription = (episode, condition) => {
  switch (condition.category) {
    case PermissionCategory.EXCLUSIVE_CONTENT:
      return 'Give your paying subscribers an ad-free listening experience.';

    case PermissionCategory.EARLY_RELEASE: {
      const dayCount = getEarlyReleaseDayCount(condition);
      return `Available to everyone ${dayCount} ${dayCount === 1 ? 'day' : 'days'} after release`;
    }

    default:
      return `Unknown category ${condition.category}`;
  }
};

const createRemoveConditionHandler = (episode, condition) => {
  return () => {
    const { conditions } = episode.data;

    updateEntity(
      Episode,
      episode.data.merge({
        conditions: conditions.filter(c => c.category !== condition.category),
      })
    );
  };
};

const renderConditionListItems = episode => {
  const conditions = resolve(episode, 'data.conditions', []);

  return conditions.map(condition => {
    return (
      <ListItem key={condition.category}>
        <Text mb={0}>
          <strong>{getCategoryLabel(condition.category)}</strong>:{' '}
          {getConditionShortDescription(episode, condition)}
        </Text>
        <Button
          variant="tertiary"
          intent="danger"
          glyph={TimesLightGlyph}
          size="small"
          title="Remove condition"
          onClick={createRemoveConditionHandler(episode, condition)}
        />
      </ListItem>
    );
  });
};

const renderAddConditionItem = episode => {
  const canAddCondition = getAvailableCategories(episode).length > 0;
  if (!canAddCondition) {
    return null;
  }

  const conditions = resolve(episode, 'data.conditions', []);
  const hasConditions = conditions.length > 0;
  const hasPublishDate = resolve(episode, 'data.publishDate', null) !== null;

  const noConditionsMessage = hasConditions
    ? null
    : 'This episode does not currently have any conditions applied.';

  const noPublishDateMessage = hasPublishDate
    ? null
    : 'You must select a publish date before you can add conditions';

  return (
    <ListItem>
      <Text mb={0}>{noPublishDateMessage || noConditionsMessage || ''}</Text>
      <Button
        variant="secondary"
        size="small"
        disabled={!hasPublishDate}
        glyph={PlusLightGlyph}
        onClick={() => showAddNewConditionDialog(episode)}
      >
        Add Condition
      </Button>
    </ListItem>
  );
};

const ConditionsList = ({ episode }) => {
  if (episode.pending) {
    return null;
  }

  return (
    <List>
      {renderConditionListItems(episode)}
      {renderAddConditionItem(episode)}
    </List>
  );
};

ConditionsList.propTypes = {
  episode: resourceProp(Episode),
};

export default ConditionsList;
